/**
 * What everything sounds like, and the only file that knows.
 *
 * **This is the swap.** Recorded audio arrives, gets keyed by the names below,
 * and this file is replaced. Nothing else moves: not the falloff, not the
 * occlusion, not the soundboard, not a call site — because nothing else in this
 * layer knows what a footstep is made of, only that there is one called
 * `step.marble` and how loud it should be from here.
 *
 * Until then it is oscillators and filtered noise, because greybox rules are
 * primitives and flat colours, and that holds for the ears as well as the eyes.
 * A synthesised footstep is placeholder the way a box is placeholder: honest
 * about being a stand-in, and carrying the one piece of information it is
 * actually there to carry.
 *
 * ---------------------------------------------------------------------------
 * THE RECIPES ARE NUMBERS, NOT NODES
 *
 * Nothing here touches the Web Audio API. A voice is a plain object saying what
 * the sound is made of, and `synth.js` is the only file that turns one into
 * something audible. That split is what lets the whole catalogue be read,
 * compared and checked without a browser, and it is why swapping in samples is
 * a change to one file rather than a change to every sound.
 * ---------------------------------------------------------------------------
 *
 * A recipe may carry:
 *
 *   noise    a burst of filtered noise: `{ type, hz, to, q }`
 *   tones    oscillators: `[{ type, hz, to, gain, delay }]`
 *   attack   seconds to full
 *   decay    seconds to nothing
 *   hold     seconds at full between the two
 *   gain     the voice's own trim, before distance and walls
 *   loop     true for something that runs until it is told to stop
 */

/**
 * Footsteps, one per surface the building is floored with.
 *
 * The surface is not decoration. Somebody in blacks on the marble is an offence
 * the moment they are noticed, and the marble is the one floor in the building
 * that announces you: bright, long-tailed, and audible a long way down a hard
 * room. Carpet in the ballroom is the opposite and for the same reason. The
 * floors already tell the player where they are allowed to be; now they say it
 * out loud.
 */
const STEPS = {
  concrete: {
    noise: { type: 'bandpass', hz: 1150, to: 460, q: 0.9 },
    tones: [{ type: 'sine', hz: 84, to: 58, gain: 0.5 }],
    attack: 0.002, decay: 0.075, gain: 0.5,
  },
  marble: {
    // Hard, bright, and it rings. The floor you are not supposed to be on.
    noise: { type: 'bandpass', hz: 2700, to: 1250, q: 1.4 },
    tones: [{ type: 'triangle', hz: 2050, to: 1780, gain: 0.16 }, { type: 'sine', hz: 96, gain: 0.3 }],
    attack: 0.001, decay: 0.19, gain: 0.62,
  },
  tile: {
    noise: { type: 'bandpass', hz: 2000, to: 900, q: 1.1 },
    tones: [{ type: 'sine', hz: 92, to: 70, gain: 0.34 }],
    attack: 0.002, decay: 0.11, gain: 0.52,
  },
  vinyl: {
    noise: { type: 'bandpass', hz: 760, to: 340, q: 0.8 },
    tones: [{ type: 'sine', hz: 78, to: 60, gain: 0.42 }],
    attack: 0.003, decay: 0.06, gain: 0.44,
  },
  carpet: {
    // The ballroom. You can cross it during a function without announcing it,
    // which is most of why the ballroom is crossable during a function.
    noise: { type: 'lowpass', hz: 520, q: 0.7 },
    tones: [{ type: 'sine', hz: 70, to: 54, gain: 0.3 }],
    attack: 0.004, decay: 0.05, gain: 0.3,
  },
  asphalt: {
    noise: { type: 'bandpass', hz: 980, to: 420, q: 0.7 },
    tones: [{ type: 'sine', hz: 76, to: 58, gain: 0.34 }],
    attack: 0.003, decay: 0.085, gain: 0.46,
  },
  gravel: {
    // The Caytons. Nothing out there is quiet underfoot.
    noise: { type: 'highpass', hz: 1700, q: 0.6 },
    tones: [{ type: 'sine', hz: 88, gain: 0.2 }],
    attack: 0.002, decay: 0.135, gain: 0.5,
  },
  grass: {
    // The lawn. A soft brush and not much else, which is what a lawn is for.
    noise: { type: 'lowpass', hz: 1300, q: 0.5 },
    tones: [{ type: 'sine', hz: 64, to: 50, gain: 0.18 }],
    attack: 0.006, decay: 0.07, gain: 0.26,
  },
  water: {
    // The pool. A slosh, low and wide.
    noise: { type: 'lowpass', hz: 900, to: 380, q: 0.6 },
    tones: [{ type: 'sine', hz: 58, to: 44, gain: 0.22 }],
    attack: 0.01, decay: 0.18, gain: 0.4,
  },
};

/** Every floor in the building, and what is under your feet when you are lost. */
export const SURFACES = Object.freeze(Object.keys(STEPS));
export const DEFAULT_SURFACE = 'concrete';

/**
 * The whole catalogue.
 *
 * Keyed by name, and the names are what the soundboard asks for. A footstep is
 * `step.<surface>`; everything else is the thing it is.
 */
export const VOICES = Object.freeze({
  ...Object.fromEntries(Object.entries(STEPS).map(([name, recipe]) => [`step.${name}`, recipe])),

  // --- hands ---------------------------------------------------------------
  /** Taking something off a shelf. Cloth and weight, no impact. */
  lift: {
    noise: { type: 'bandpass', hz: 640, to: 280, q: 0.6 },
    attack: 0.012, hold: 0.02, decay: 0.12, gain: 0.34,
  },
  /**
   * Putting something down, which is a verb with a rule attached.
   *
   * A drop is the counterplay to being seen holding a thing, and it is
   * deliberately never a throw. So it is deliberately not a crash either: a
   * short, controlled, low set-down that nobody could mistake for a breakage.
   */
  setdown: {
    noise: { type: 'lowpass', hz: 900, q: 0.8 },
    tones: [{ type: 'sine', hz: 110, to: 72, gain: 0.5 }],
    attack: 0.001, decay: 0.14, gain: 0.44,
  },

  // --- things in the air ---------------------------------------------------
  /** The arm, not the landing. Air and effort. */
  throw: {
    noise: { type: 'bandpass', hz: 520, to: 1500, q: 0.5 },
    attack: 0.05, decay: 0.1, gain: 0.3,
  },
  /** Something arriving. The weight is in the distance, not in here. */
  impact: {
    noise: { type: 'lowpass', hz: 1400, q: 1 },
    tones: [{ type: 'sine', hz: 140, to: 62, gain: 0.7 }],
    attack: 0.001, decay: 0.22, gain: 0.7,
  },
  /**
   * Something that stopped being one object.
   *
   * The loudest thing in the catalogue, because it is the loudest thing in the
   * rules: a break carries forty feet by default and gets a witness a long way
   * before anything else does.
   */
  break: {
    noise: { type: 'highpass', hz: 2400, q: 0.5 },
    tones: [
      { type: 'triangle', hz: 3100, to: 2400, gain: 0.3 },
      { type: 'triangle', hz: 4400, to: 3600, gain: 0.22, delay: 0.015 },
      { type: 'sine', hz: 180, to: 90, gain: 0.4 },
    ],
    attack: 0.001, decay: 0.5, gain: 0.9,
  },

  // --- the cart ------------------------------------------------------------
  /**
   * The bed and the load, running. Loops for as long as the cart is moving.
   *
   * Low and broad, because this is the part that goes through a wall. The
   * castors are separate and do not, which is the honest version of the thing
   * the player actually needs: from the next room you can tell a cart is
   * moving, and only in the corridor with it can you tell how fast.
   */
  'cart.roll': {
    noise: { type: 'lowpass', hz: 340, q: 0.9 },
    attack: 0.25, decay: 0.4, gain: 0.5, loop: true,
  },
  /** One castor, once. Fired in time with the wheels, so speed is audible. */
  'cart.castor': {
    noise: { type: 'bandpass', hz: 2600, to: 1900, q: 3.2 },
    attack: 0.001, decay: 0.045, gain: 0.3,
  },
  /** A loaded cart going over. Everything on it arrives at once. */
  'cart.tip': {
    noise: { type: 'bandpass', hz: 1500, to: 400, q: 0.5 },
    tones: [
      { type: 'sine', hz: 130, to: 48, gain: 0.8 },
      { type: 'triangle', hz: 900, to: 300, gain: 0.3, delay: 0.04 },
    ],
    attack: 0.001, decay: 0.7, gain: 0.95,
  },

  // --- the building --------------------------------------------------------
  /** A door. Latch first, then the leaf. */
  door: {
    noise: { type: 'bandpass', hz: 1800, to: 700, q: 1.6 },
    tones: [{ type: 'sine', hz: 120, to: 84, gain: 0.35, delay: 0.06 }],
    attack: 0.001, decay: 0.3, gain: 0.5,
  },
  /**
   * The time clock. A mechanical punch and then the bell.
   *
   * It gets a real note of its own because it is the one sound in the game that
   * means the shift started, and a shift starting should be audible from the
   * alcove door.
   */
  'clock.punch': {
    noise: { type: 'bandpass', hz: 900, to: 300, q: 2.4 },
    tones: [
      { type: 'square', hz: 160, to: 120, gain: 0.3 },
      { type: 'sine', hz: 1860, gain: 0.34, delay: 0.09 },
      { type: 'sine', hz: 2790, gain: 0.14, delay: 0.09 },
    ],
    attack: 0.001, decay: 0.75, gain: 0.55,
  },
  /** The BEO, turned over. Paper and nothing else. */
  page: {
    noise: { type: 'highpass', hz: 2200, q: 0.4 },
    attack: 0.006, hold: 0.03, decay: 0.16, gain: 0.26,
  },

  // --- the body ------------------------------------------------------------
  /** Getting under something, or out from under it. Cloth on cloth. */
  rustle: {
    noise: { type: 'bandpass', hz: 1400, to: 3000, q: 0.5 },
    attack: 0.02, hold: 0.04, decay: 0.2, gain: 0.3,
  },
  /** Hands and boots on something you are not meant to be climbing. */
  climb: {
    noise: { type: 'bandpass', hz: 780, to: 1600, q: 0.8 },
    tones: [{ type: 'sine', hz: 96, gain: 0.24 }],
    attack: 0.01, hold: 0.05, decay: 0.28, gain: 0.42,
  },
  /** Something out in the dark deciding about you. */
  charge: {
    noise: { type: 'bandpass', hz: 420, to: 180, q: 0.7 },
    tones: [{ type: 'sawtooth', hz: 62, to: 44, gain: 0.5 }],
    attack: 0.03, hold: 0.1, decay: 0.6, gain: 0.7,
  },
});

/**
 * The rooms, underneath everything.
 *
 * Four beds, one per zone, and the brief they are written to is that they must
 * not mask anything above. So all four are low, narrow and slow: they live
 * under about five hundred hertz, where nothing in the catalogue above has its
 * information. A footstep on marble is two and a half kilohertz and a castor is
 * two and a half; they do not compete, they sit on top.
 *
 * `restricted` is the office, and it is the quietest thing in the building on
 * purpose: the room you should not be standing in is the room where you can
 * hear somebody coming.
 */
export const AMBIENCE = Object.freeze({
  /** Back of house. Plant, extract, the fridges. */
  boh: {
    noise: { type: 'lowpass', hz: 300, q: 0.6 },
    tones: [{ type: 'sine', hz: 58, gain: 0.1 }, { type: 'sine', hz: 87, gain: 0.05 }],
    gain: 0.07, loop: true, attack: 1.4, decay: 1.4,
  },
  /** The ballroom. A big empty room is not silent, it is wide. */
  event: {
    noise: { type: 'lowpass', hz: 200, q: 0.5 },
    tones: [{ type: 'sine', hz: 44, gain: 0.12 }],
    gain: 0.055, loop: true, attack: 2, decay: 2,
  },
  /** Front of house. Air handling and a room with people in it somewhere. */
  foh: {
    noise: { type: 'bandpass', hz: 260, to: 180, q: 0.4 },
    tones: [{ type: 'sine', hz: 70, gain: 0.08 }],
    gain: 0.06, loop: true, attack: 1.6, decay: 1.6,
  },
  /** Outside. Wind, and the desert not caring. */
  outside: {
    noise: { type: 'lowpass', hz: 420, q: 0.4 },
    gain: 0.075, loop: true, attack: 2.4, decay: 2.4,
  },
  /** The office. Almost nothing, so that anything else is everything. */
  restricted: {
    noise: { type: 'lowpass', hz: 160, q: 0.5 },
    gain: 0.03, loop: true, attack: 1.2, decay: 1.2,
  },
});

/**
 * How far each audio-only sound carries, in metres.
 *
 * Only for the sounds the simulation has no opinion about. Anything that is a
 * noise event arrives with its own radius and uses that one — the radius is the
 * rule, and inventing a second number for the same sound would be the audio
 * layer quietly disagreeing with the thing it exists to explain.
 *
 * These are the rest: footsteps, doors, the clock, the sheet, your own hands.
 * They are ordinary-hearing distances rather than rule distances, and the
 * spread between them is the one piece of information they carry — the marble
 * announces you for forty feet and the ballroom carpet does not announce you at
 * all, which is the same sentence the floors have been saying in colour since
 * the blockout.
 */
const FT = 0.3048;
export const REACH = Object.freeze({
  'step.concrete': FT * 26,
  'step.marble': FT * 40,
  'step.tile': FT * 30,
  'step.vinyl': FT * 20,
  'step.carpet': FT * 11,
  'step.asphalt': FT * 22,
  'step.gravel': FT * 30,
  'step.grass': FT * 9,
  'step.water': FT * 18,

  lift: FT * 10,
  setdown: FT * 14,
  door: FT * 34,
  'clock.punch': FT * 44,
  page: FT * 7,
  /* A pushed cart, which the simulation never emits a noise for. A ridden one
   * does, and uses the radius that comes with it instead of this. */
  'cart.roll': FT * 34,
  'cart.castor': FT * 22,
});

/** Every name the soundboard may ask for. */
export const NAMES = Object.freeze(Object.keys(VOICES));
export const ZONES = Object.freeze(Object.keys(AMBIENCE));

/** A recipe by name, or null. Never throws: a missing sound is a silence. */
export function voiceFor(name) {
  return VOICES[name] ?? null;
}

/** The footstep for a surface, falling back to concrete for anywhere unnamed. */
export function stepFor(surface) {
  return VOICES[`step.${surface}`] ? `step.${surface}` : `step.${DEFAULT_SURFACE}`;
}

/**
 * How long a recipe lasts, in seconds.
 *
 * Needed by the plumbing to know when to let a one-shot go, and useful on its
 * own: a catalogue where something runs for four seconds by accident is a
 * catalogue with a stuck voice in it, and that is easier to see as a number
 * than to hear.
 */
export function lengthOf(recipe) {
  if (!recipe) return 0;
  const tail = Math.max(0, ...(recipe.tones ?? []).map((tone) => tone.delay ?? 0));
  return (recipe.attack ?? 0.005) + (recipe.hold ?? 0) + (recipe.decay ?? 0.1) + tail;
}
